import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ExportModal from './ExportModal';
import { useAuth } from './AuthContext';
import { AgGridReact } from 'ag-grid-react';
import MeshTreeFilter from './MeshTreeFilter';
import MeshSunburstFilter from './MeshSunburstFilter';
import MeshClassificationFilter from './MeshClassificationFilter';
import CcGoTermBarChart from './CcGoTermBarChart';
import CcGoTermTreeFilter from './CcGoTermTreeFilter';
import BpGoTermBarChart from './BpGoTermBarChart';
import CC_GO_TERMS from './ccGoTerms';
import PlatformDonutChart, { PLATFORM_GROUPS } from './PlatformDonutChart';
import he from 'he';
import { ModuleRegistry, AllCommunityModule, themeQuartz } from 'ag-grid-community';
import './css/custom-dark-theme.css';
import './css/styles.css';
import ibmLogoDark from './img/powered-by-ibm-cloud-logo-e.png';
import AdminPanel from './admin/AdminPanel';
import Navbar from './Navbar';
import respiratoryListUrl from './proteins-associated-with-respiratory-tract-conditions-including-lung.list';
import tissueEnrichedIcon from './icons/tissue_enriched.svg';
import tissueEnhancedIcon from './icons/tissue_enhanced.svg';
import groupEnrichedIcon from './icons/group_enriched.svg';
import lowSpecificityIcon from './icons/low_specificity.svg';
import notDetectedIcon from './icons/not_detected.svg';
import noDataIcon from './icons/no_data.svg';

ModuleRegistry.registerModules([AllCommunityModule]);

// Shared extracellular-region CC GO whitelist (single source of truth in
// ccGoTerms.js). Used to derive the "CC only" filter set, so it stays in sync
// with the whitelist instead of the stale server-precomputed goterm_cc_all_filtered.
const CC_WHITELIST_IDS = new Set(CC_GO_TERMS.map(t => t.id));
const ccTermId = t => (typeof t === 'string' ? t : (t?.go_id || t?.id || ''));

// Stable grid-option references. Passing fresh object literals for these on
// every render makes AG Grid v33 reconfigure (default columns / selection
// system) repeatedly during the multi-render data-load sequence, which delays
// the grid appearing. Defining them once keeps the reference identical.
const GRID_DEFAULT_COL_DEF = { sortable: true, resizable: true };
const GRID_ROW_SELECTION = {
  mode: 'multiRow',
  checkboxes: true,
  headerCheckbox: true,
  enableClickSelection: false,
  selectAll: 'filtered',
};

const MultiLineTooltip = props => (
  <div className="ag-tooltip" style={{ whiteSpace: 'pre' }}>
    {props.value}
  </div>
);

const SPECIFICITY_ICONS = {
  'Tissue enriched': tissueEnrichedIcon,
  'Tissue enhanced': tissueEnhancedIcon,
  'Group enriched': groupEnrichedIcon,
  'Low tissue specificity': lowSpecificityIcon,
  'Not detected': notDetectedIcon,
  '__no_lung_data__': noDataIcon,
};

const LUNG_SPECIFICITY_CATEGORIES = [
  'Tissue enriched',
  'Group enriched',
  'Tissue enhanced',
  'Low tissue specificity',
  'Not detected',
  '__no_lung_data__',
];

const VISIBLE_LUNG_SPECIFICITY_CATEGORIES = [
  'Tissue enriched',
  'Group enriched',
  'Tissue enhanced',
];

const LUNG_SPECIFICITY_LABELS = {
  'Tissue enriched': 'Lung tissue enriched',
  'Group enriched': 'Group enriched (incl. lung)',
  'Tissue enhanced': 'Lung tissue enhanced',
  'Low tissue specificity': 'Low lung tissue specificity',
  'Not detected': 'Not detected in lung',
  '__no_lung_data__': 'Specificity data missing',
};

const diseaseFilterFlag = String(process.env.REACT_APP_SHOW_DISEASE_FILTER ?? 'true').toLowerCase();
const showDiseaseFilter = !['0', 'false', 'no', 'off'].includes(diseaseFilterFlag);

export default function MainGrid() {
  const [rowData, setRowData] = useState([]);
  // 'none' | 'go' | 'diseases' | 'lung' | 'platform'
  const [activeFilterCategory, setActiveFilterCategory] = useState('none');
  // 'cc' | 'bp' — sub-mode when activeFilterCategory === 'go'
  const [goSubMode, setGoSubMode] = useState('cc');
  const [ccView, setCcView] = useState('bar'); // 'bar' | 'tree' for the CC filter
  const [meshViewMode, setMeshViewMode] = useState('tree'); // 'tree' | 'sunburst' | 'classification'
  const [meshFilterSet, setMeshFilterSet] = useState(null); // Set<uniprotId> | null
  const [meshTreeRespiratoryOnly, setMeshTreeRespiratoryOnly] = useState(true);
  const [meshClassificationRespiratoryOnly, setMeshClassificationRespiratoryOnly] = useState(true);
  const [filterBpOnly, setFilterBpOnly] = useState(false);
  const [filterCcOnly, setFilterCcOnly] = useState(false);
  const [gridApi, setGridApi] = useState(null);
  const [displayedRowCount, setDisplayedRowCount] = useState(0);
  const [selectedRowCount, setSelectedRowCount] = useState(0);
  const [notification, setNotification] = useState(null); // { id, message, type: 'success' | 'error' }
  const [showExportModal, setShowExportModal] = useState(false);

  const showNotification = (message, type = 'success') => {
    setNotification({ id: Date.now(), message, type });
  };

  useEffect(() => {
    if (!notification) return undefined;
    const t = setTimeout(() => {
      setNotification(curr => (curr && curr.id === notification.id ? null : curr));
    }, 5000);
    return () => clearTimeout(t);
  }, [notification]);
  const [respiratoryUniprotSet, setRespiratoryUniprotSet] = useState(null);
  const [filterRespiratoryOnly, setFilterRespiratoryOnly] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme === 'dark' : true; // default = dark
  });
  const [selectedDb, setSelectedDb] = useState('proteindb');
  const [allOrganNames, setAllOrganNames] = useState([]);
  const [customOrganNamesToShow, setCustomOrganNamesToShow] = useState(new Set());
  // 'none' | 'lung' | 'custom' — tissue-expression columns hidden for now (always 'none')
  const [tissueColumnMode, setTissueColumnMode] = useState('none');
  const [selectedLungSpecificities, setSelectedLungSpecificities] = useState(new Set());
  const [selectedCcGoTerms, setSelectedCcGoTerms] = useState(new Set());
  const [selectedBpGoTerms, setSelectedBpGoTerms] = useState(new Set());
  const [selectedPlatforms, setSelectedPlatforms] = useState(new Set());
  const gridRef = useRef(null);
  const quickFilterRef = useRef(null);

  const handleResetToHome = () => {
    setActiveFilterCategory('none');
    setGoSubMode('cc');
    setMeshViewMode('tree');
    setMeshFilterSet(null);
    setMeshTreeRespiratoryOnly(true);
    setMeshClassificationRespiratoryOnly(true);
    setFilterBpOnly(false);
    setFilterCcOnly(false);
    setFilterRespiratoryOnly(false);
    setTissueColumnMode('none');
    setCustomOrganNamesToShow(new Set());
    setSelectedLungSpecificities(new Set());
    setSelectedCcGoTerms(new Set());
    setSelectedBpGoTerms(new Set());
    setSelectedPlatforms(new Set());
    setSelectedDb('proteindb');

    if (quickFilterRef.current) quickFilterRef.current.value = '';
    if (gridApi) {
      gridApi.setGridOption('quickFilterText', '');
      gridApi.setFilterModel(null);
      gridApi.deselectAll();
      gridApi.onFilterChanged();
    }
  };

  const { user } = useAuth();
  const navigate = useNavigate();
  const [showAdmin, setShowAdmin] = useState(false);
  const isAdmin = user?.system_role === 'admin';

  const baseColumnDefs = [
    {
      headerName: 'UniProt ID',
      field: 'uniprotId',
      width: 155,
      filter: true
    },
    {
      headerName: 'Target Full Name',
      field: 'targetFullName',
      filter: true,
      flex: 1,
      tooltipField: 'targetFullName',
      cellStyle: { display: 'flex', alignItems: 'center' },
      cellRenderer: params => {
        if (!params.data) return null;
        const { uniprotId } = params.data;
        const name = params.value;
        return (
          <a
            className="a-target-full-name"
            href={`/protein/${uniprotId}/`}
            target="_blank"
            rel="noreferrer"
            onClick={e => e.stopPropagation()}
            style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%', height: '100%', textDecoration: 'none' }}
          >
            <span className="material-symbols-outlined protein-list-link-icon">
              open_in_new
            </span>
            {name}
          </a>
        );
      }
    },
    { headerName: 'Gene Symbol', field: 'geneSymbol', width: 145, filter: true, tooltipField: 'geneSymbol' },
    { headerName: 'Gene Synonyms', field: 'geneSynonyms', width: 160, filter: true, tooltipField: 'geneSynonyms', valueFormatter: params => params.value || '—' },
    { headerName: 'In-House Studies', field: 'numInhouse', width: 150, filter: 'agNumberColumnFilter' },
    { headerName: 'Published Studies', field: 'numPublished', width: 150, filter: 'agNumberColumnFilter' },
    {
      headerName: 'Platforms',
      field: 'platforms',
      // flex: 1,
      minWidth: 150,
      filter: true,
      valueFormatter: params => {
        const raw = params.value || '';
        const items = raw.split(',').map(s => s.trim()).filter(Boolean);
        const out = [];
        let hasSoma = false;
        for (const item of items) {
          if (item === 'SomaScan' || item === 'SomaScan 7k') {
            if (!hasSoma) {
              out.push('SomaScan');
              hasSoma = true;
            }
          } else {
            out.push(item);
          }
        }
        return out.join(', ');
      },
      tooltipValueGetter: params => {
        const raw = params.value || '';
        const items = raw.split(',').map(s => s.trim()).filter(Boolean);
        return items.join('\n');
      },
      tooltipComponent: MultiLineTooltip
    },
    {
      headerName: 'DS ⓘ',
      headerTooltip: 'Detectibility Score (how prevalent the detection of the protein is in studies)',
      field: 'grade',
      width: 110,
      filter: 'agNumberColumnFilter',
      cellStyle: { textAlign: 'right' },
      cellRenderer: params => {
        if (!params.value) return '';
        const starCount = Math.round(params.value);
        return '★'.repeat(starCount);
      }
    },
    {
      headerName: 'Diseases',
      field: 'diseases',
      hide: true,
      valueFormatter: params =>
        Array.isArray(params.value) ? params.value.join(', ') : params.value
    }
  ];

  const columnDefs = useMemo(() => {
    const cols = [...baseColumnDefs];
    let tissuesForColumns;
    if (tissueColumnMode === 'none') {
      tissuesForColumns = new Set();
    } else if (tissueColumnMode === 'lung') {
      tissuesForColumns = new Set(allOrganNames.includes('Lung') ? ['Lung'] : []);
    } else {
      tissuesForColumns = customOrganNamesToShow;
    }
    for (const tissue of allOrganNames) {
      if (!tissuesForColumns.has(tissue)) continue;
      cols.push({
        headerName: `${tissue} (nTPM)`,
        field: `te_${tissue}`,
        width: 210,
        sortable: true,
        resizable: true,
        filter: 'agNumberColumnFilter',
        cellStyle: { textAlign: 'right' },
        cellRenderer: params => {
          const nTPM = params.value;
          const specificity = params.data?.[`ts_${tissue}`];
          const icon = SPECIFICITY_ICONS[specificity];
          return (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
              <span>{nTPM != null ? `${Number(nTPM).toFixed(1)} nTPM` : '—'}</span>
              {icon && (
                <img
                  src={icon}
                  alt={specificity}
                  title={specificity}
                  style={{ width: 14, height: 14, display: 'inline-block' }}
                />
              )}
            </span>
          );
        }
      });
    }
    return cols;
  }, [allOrganNames, customOrganNamesToShow, tissueColumnMode]); // eslint-disable-line

  function capitalize(word) {
    if (!word) return word;
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }

  function reCaseString(str) {
    const words = str.split(/\s+/);
    let recaseAll = false;
    if (words.length > 1 && str === str.toUpperCase()) recaseAll = true;
    if (!recaseAll) {
      for (let word of words) {
        if (word.length > 4 && word === word.toUpperCase()) {
          recaseAll = true;
          break;
        }
      }
    }
    if (!recaseAll) {
      for (let word of words) {
        const alphabets = word.match(/[a-zA-Z]/g);
        if (alphabets && alphabets.length > 1 && word[0] === word[0].toLowerCase()) {
          recaseAll = true;
          break;
        }
      }
    }
    if (recaseAll) {
      return words.map(capitalize).join(' ');
    }
    if (/[a-z]/.test(str)) {
      return words
        .map(word => (word === word.toUpperCase() ? word : capitalize(word)))
        .join(' ');
    }
    return str;
  }

  useEffect(() => {
    // This fetch uses the '/api/flask' proxy and includes credentials (session cookie)
    fetch('/api/fastapi/proteins-data', { credentials: 'include' })
      .then(res => res.text())
      .then(text => {
        const lines = text.trim().split('\n');
        const data = lines.map(line => JSON.parse(line));
        const allTissues = new Set();
        const baseRows = data.map(entry => {
          const inhouse = entry.detections.filter(d => d.hasOwnProperty('inhouse_study'));
          const numInhouse = inhouse.length;
          const numPublished = entry.detections.filter(d => d.hasOwnProperty('published_study')).length;
          const platformSet = new Set();
          const normalizePlatform = value => {
            if (!value || typeof value !== 'string') return null;
            const trimmed = value.trim();
            if (!trimmed) return null;
            if (/^somascan\s*7k$/i.test(trimmed)) return 'SomaScan 7k';
            if (/^somascan$/i.test(trimmed)) return 'SomaScan';
            if (/^ms$/i.test(trimmed)) return 'MS';
            return trimmed;
          };
          const addPlatformValue = value => {
            if (!value) return;
            if (typeof value === 'string') {
              value
                .split(/[;,]/)
                .map(v => normalizePlatform(v))
                .filter(Boolean)
                .forEach(v => platformSet.add(v));
              return;
            }
            const normalized = normalizePlatform(value);
            if (normalized) platformSet.add(normalized);
          };
          entry.detections.forEach(d => {
            addPlatformValue(d.analysis?.platform);
            const pubPlatforms = d.published_study?.platforms;
            if (Array.isArray(pubPlatforms)) {
              pubPlatforms.forEach(p => addPlatformValue(p));
            } else {
              addPlatformValue(pubPlatforms);
            }
          });
          const platforms = Array.from(platformSet).sort().join(', ');
          const hasMs = platformSet.has('MS');
          const hasSomaUnspec = platformSet.has('SomaScan');
          const hasSoma7k = platformSet.has('SomaScan 7k');
          const hasSomaFamily = hasSomaUnspec || hasSoma7k;
          let platformSubcategory = null;
          if (hasMs && hasSomaFamily) {
            platformSubcategory = hasSoma7k ? 'Both_7k' : 'Both_unspec';
          } else if (hasMs) {
            platformSubcategory = 'MS';
          } else if (hasSomaFamily) {
            platformSubcategory = hasSoma7k ? 'SomaScan_7k' : 'SomaScan_unspec';
          }
          const geneSynonyms = entry.info.gene_synonyms?.join(', ') || '';
          const diseasesArr = entry.info.disgenet
            ? entry.info.disgenet.map(d => he.decode(reCaseString(d.name)))
            : [];
          const gotermBpAllFiltered = Array.isArray(entry.info.goterm_bp_all_filtered)
            ? entry.info.goterm_bp_all_filtered
            : [];
          const gotermCcAll = Array.isArray(entry.info.goterm_cc_all)
            ? entry.info.goterm_cc_all
            : (Array.isArray(entry.info.goterm_cc_all_filtered) ? entry.info.goterm_cc_all_filtered : []);
          // Derive the "filtered" CC set from the shared whitelist rather than the
          // stale server-precomputed field, so the "CC only" toggle matches the
          // current extracellular-region whitelist (ccGoTerms.js).
          const gotermCcAllFiltered = gotermCcAll.filter(t => CC_WHITELIST_IDS.has(ccTermId(t)));
          const gotermBpAll = Array.isArray(entry.info.goterm_bp_all)
            ? entry.info.goterm_bp_all
            : gotermBpAllFiltered;
          const tissueExpression = Array.isArray(entry.info.tissue_expression) ? entry.info.tissue_expression : [];
          const tissueFields = {};
          tissueExpression.forEach(te => {
            allTissues.add(te.tissue);
            tissueFields[`te_${te.tissue}`] = te.value;
            tissueFields[`ts_${te.tissue}`] = te.specificity;
          });
          return {
            uniprotId: entry.uniprot_id,
            targetFullName: entry.info.gene_description,
            geneSymbol: entry.info.gene || '',
            numInhouse,
            numPublished,
            platforms,
            platformSubcategory,
            geneSynonyms,
            diseases: diseasesArr,
            gotermBpAllFiltered,
            gotermCcAllFiltered,
            gotermCcAll,
            gotermBpAll,
            tissueExpression,
            ...tissueFields,
            totalStudies: numInhouse + numPublished
          };
        });
        const maxTotalStudies = baseRows.reduce((max, row) => Math.max(max, row.totalStudies), 0);
        const formatted = baseRows.map(row => {
          let grade = '';
          if (maxTotalStudies > 0) {
            if (maxTotalStudies === 1) {
              grade = 1;
            } else {
              const normalized = (row.totalStudies - 1) / (maxTotalStudies - 1);
              grade = 1 + normalized * 4;
            }
          }
          return {
            ...row,
            grade
          };
        });
        setRowData(formatted);
        setAllOrganNames(Array.from(allTissues).sort());
      })
      .catch(err => console.error('Error fetching data:', err));
  }, []);

  useEffect(() => {
    fetch(respiratoryListUrl)
      .then(res => res.text())
      .then(text => {
        const ids = text
          .split(/\r?\n/)
          .map(line => line.trim().toUpperCase())
          .filter(Boolean);
        setRespiratoryUniprotSet(new Set(ids));
      })
      .catch(err => console.error('Error fetching respiratory protein list:', err));
  }, []);

  const updateDisplayedRowCount = api => {
    if (!api) return;
    setDisplayedRowCount(api.getDisplayedRowCount());
  };

  const onGridReady = params => {
    setGridApi(params.api);
    updateDisplayedRowCount(params.api);
  };

  const onSelectionChanged = params => {
    setSelectedRowCount(params.api.getSelectedNodes().length);
  };

  const handleCopySelectedRows = async () => {
    if (!gridApi) return;

    const selectedIds = new Set(
      gridApi.getSelectedNodes().map(node => node.data?.uniprotId).filter(Boolean)
    );
    const copyAllVisible = selectedIds.size === 0;

    const exportColumns = columnDefs.filter(col => col.field && !col.hide);
    const headerLine = exportColumns.map(col => col.headerName || col.field).join('\t');
    const rowLines = [];

    gridApi.forEachNodeAfterFilterAndSort(node => {
      const row = node.data;
      if (!row) return;
      if (!copyAllVisible && !selectedIds.has(row.uniprotId)) return;
      rowLines.push(
        exportColumns
          .map(col => {
            let value = row[col.field];
            if (col.field === 'grade' && value) {
              value = '★'.repeat(Math.round(value));
            }
            if (value == null) return '';
            if (Array.isArray(value)) value = value.join(', ');
            return String(value).replace(/\r?\n/g, ' ').replace(/\t/g, ' ');
          })
          .join('\t')
      );
    });

    if (rowLines.length === 0) {
      showNotification('Nothing to copy — the protein list is empty.', 'error');
      return;
    }

    try {
      await navigator.clipboard.writeText([headerLine, ...rowLines].join('\n'));
      const noun = rowLines.length === 1 ? 'protein' : 'proteins';
      showNotification(
        copyAllVisible
          ? `Copied all ${rowLines.length} ${noun} currently shown to the clipboard (TSV).`
          : `Copied ${rowLines.length} selected ${noun} to the clipboard (TSV).`
      );
    } catch (err) {
      console.error('Clipboard copy failed:', err);
      showNotification('Clipboard copy failed. Your browser may have blocked the action.', 'error');
    }
  };

  const isExternalFilterPresent = () => (
    (showDiseaseFilter && activeFilterCategory === 'diseases' && meshFilterSet !== null) ||
    filterBpOnly || filterCcOnly || filterRespiratoryOnly ||
    selectedLungSpecificities.size > 0 ||
    selectedCcGoTerms.size > 0 ||
    selectedBpGoTerms.size > 0 ||
    selectedPlatforms.size > 0
  );
  const doesExternalFilterPass = node => {
    const data = node.data || {};
    const id = String(data.uniprotId || '').toUpperCase();

    if (filterRespiratoryOnly) {
      if (!respiratoryUniprotSet) return false;
      if (!respiratoryUniprotSet.has(id)) return false;
    }
    if (filterBpOnly && (!data.gotermBpAllFiltered || data.gotermBpAllFiltered.length === 0)) {
      return false;
    }
    if (filterCcOnly && (!data.gotermCcAllFiltered || data.gotermCcAllFiltered.length === 0)) {
      return false;
    }
    if (selectedLungSpecificities.size > 0) {
      const lungSpecificity = data['ts_Lung'] || '__no_lung_data__';
      if (!selectedLungSpecificities.has(lungSpecificity)) return false;
    }
    if (selectedCcGoTerms.size > 0) {
      const ccTerms = Array.isArray(data.gotermCcAll) ? data.gotermCcAll : [];
      const hasMatch = ccTerms.some(t => {
        const id = typeof t === 'string' ? t : (t?.go_id || t?.id || '');
        return selectedCcGoTerms.has(id);
      });
      if (!hasMatch) return false;
    }
    if (selectedBpGoTerms.size > 0) {
      const bpTerms = Array.isArray(data.gotermBpAll) ? data.gotermBpAll : [];
      const hasMatch = bpTerms.some(t => {
        const id = typeof t === 'string' ? t : (t?.go_id || t?.id || '');
        return selectedBpGoTerms.has(id);
      });
      if (!hasMatch) return false;
    }
    if (selectedPlatforms.size > 0) {
      const sub = data.platformSubcategory;
      if (!sub || !selectedPlatforms.has(sub)) return false;
    }

    if (!showDiseaseFilter) return true;
    if (activeFilterCategory !== 'diseases') return true;
    if (meshFilterSet === null) return true;
    return meshFilterSet.has(data.uniprotId) || meshFilterSet.has(id);
  };

  const onMeshFilterChange = (set) => {
    setMeshFilterSet(set);
    gridApi?.onFilterChanged();
  };

  const handleCategoryClick = (cat) => {
    // Always clear all category-specific filter selections; only the newly
    // active category may then accumulate selections.
    setSelectedCcGoTerms(new Set());
    setSelectedBpGoTerms(new Set());
    setSelectedLungSpecificities(new Set());
    setSelectedPlatforms(new Set());
    setMeshFilterSet(null);

    setActiveFilterCategory(prev => (prev === cat ? 'none' : cat));
    gridApi?.onFilterChanged();
  };
  const onBpFilterToggle = e => {
    setFilterBpOnly(e.target.checked);
    gridApi?.onFilterChanged();
  };
  const onCcFilterToggle = e => {
    setFilterCcOnly(e.target.checked);
    gridApi?.onFilterChanged();
  };
  const onRespiratoryFilterToggle = e => {
    setFilterRespiratoryOnly(e.target.checked);
    gridApi?.onFilterChanged();
  };
  const onLungSpecificityToggle = (category, checked) => {
    setSelectedLungSpecificities(prev => {
      const next = new Set(prev);
      if (checked) next.add(category);
      else next.delete(category);
      return next;
    });
    gridApi?.onFilterChanged();
  };
  const onCcGoTermToggle = (goId, checked) => {
    setSelectedCcGoTerms(prev => {
      const next = new Set(prev);
      if (checked) next.add(goId);
      else next.delete(goId);
      return next;
    });
    gridApi?.onFilterChanged();
  };
  // Graph view: single selection — replace the CC filter with exactly these ids
  // (a term + its sub-terms), or clear it when passed an empty list.
  const onCcGoSetSelection = (goIds) => {
    setSelectedCcGoTerms(new Set(goIds));
    gridApi?.onFilterChanged();
  };
  const onBpGoTermToggle = (goId, checked) => {
    setSelectedBpGoTerms(prev => {
      const next = new Set(prev);
      if (checked) next.add(goId);
      else next.delete(goId);
      return next;
    });
    gridApi?.onFilterChanged();
  };
  const onPlatformSubToggle = (subId, checked) => {
    setSelectedPlatforms(prev => {
      const next = new Set(prev);
      if (checked) next.add(subId);
      else next.delete(subId);
      return next;
    });
    gridApi?.onFilterChanged();
  };
  const onPlatformGroupToggle = (groupId) => {
    const group = PLATFORM_GROUPS.find(g => g.id === groupId);
    if (!group) return;
    const subIds = group.subs.map(s => s.id);
    setSelectedPlatforms(prev => {
      const allSelected = subIds.every(id => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        subIds.forEach(id => next.delete(id));
      } else {
        subIds.forEach(id => next.add(id));
      }
      return next;
    });
    gridApi?.onFilterChanged();
  };

  const toggleOrganColumn = (tissue, checked) => {
    setCustomOrganNamesToShow(prev => {
      const next = new Set(prev);
      if (checked) next.add(tissue);
      else next.delete(tissue);
      return next;
    });
  };

  const onQuickFilterChange = e => {
    gridApi?.setGridOption('quickFilterText', e.target.value);
  };

  const handleThemeToggle = () => setDarkMode(prev => !prev);

  useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
    document.body.classList.toggle('light-mode', !darkMode);
    document.body.dataset.agThemeMode = darkMode ? 'dark' : 'light';
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    updateDisplayedRowCount(gridApi);
  }, [gridApi, rowData]);

  const rowDataKnownIds = useMemo(
    () => rowData.length > 0 ? new Set(rowData.map(r => r.uniprotId)) : null,
    [rowData]
  );

  // Memoize the theme by darkMode. themeQuartz.withParams() builds a new theme
  // object on every call; with the v33 Theming API an unstable `theme` prop
  // re-injects the grid CSS on every render, which (together with the several
  // re-renders that fire while data loads) makes the grid feel slow to appear.
  const gridTheme = useMemo(
    () =>
      darkMode
        ? themeQuartz.withParams({
            chromeBackgroundColor: '#333',
            headerTextColor: '#fff',
            cellTextColor: '#ddd'
          })
        : themeQuartz.withParams({
            chromeBackgroundColor: '#fff',
            headerTextColor: '#000',
            cellTextColor: '#000'
          }),
    [darkMode]
  );

  return (
    <div>
      <Navbar
        isDark={darkMode}
        onThemeToggle={handleThemeToggle}
        onHomeClick={handleResetToHome}
        actions={
          <>
            <button
              type="button"
              className="pill-btn"
              onClick={handleCopySelectedRows}
              title={
                selectedRowCount > 0
                  ? 'Copy selected protein rows to clipboard (TSV)'
                  : 'Copy all proteins currently shown to clipboard (TSV)'
              }
            >
              <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: '18px', verticalAlign: 'middle' }}>
                content_copy
              </span>{' '}
              Copy proteins
            </button>
            <button
              type="button"
              className="pill-btn"
              onClick={() => setShowExportModal(true)}
              title="Export proteins to TSV or Excel file"
            >
              <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: '18px', verticalAlign: 'middle' }}>
                save_alt
              </span>{' '}
              Export to file
            </button>
          </>
        }
      />

      <h2 id="h2-main">Small Airway Atlas™</h2>
      <p style={{ textAlign: 'center', margin: '0 0 14px 0' }}>
        Small Airway Atlas, meeting point for molecular data from the small airways.
      </p>

      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
          <div className="custom-toggle-group" aria-label="Database selection" style={{ marginTop: 0, marginBottom: '5.5px' }}>
            <button
              className={`custom-toggle-button ${selectedDb === 'proteindb' ? 'active' : ''}`}
              onClick={() => setSelectedDb('proteindb')}
              aria-pressed={selectedDb === 'proteindb'}
            >
              Proteins
            </button>
            <button className="custom-toggle-button" disabled>
              Lipids
            </button>
            <button className="custom-toggle-button" disabled>
              miRNA
            </button>
          </div>
          <button style={{ marginTop: '-4.5px' }}
            type="button"
            className="pill-btn studies-pill-btn"
            onClick={() => navigate('/studies')}
          >
            Studies
          </button>
        </div>
        <div style={{ marginTop: 8, fontSize: '0.9rem', opacity: 0.8 }}>
          Lipid, metabolite and miRNA datasets will be incorporated in future releases.
        </div>
      </div>

      <div style={{ textAlign: 'center', marginBottom: '10px' }}>
        <input
          ref={quickFilterRef}
          id="quickFilter"
          type="text"
          placeholder="Search by Protein Information..."
          onChange={onQuickFilterChange}
        />
      </div>

      <div style={{ marginBottom: '19px' }} />

      {/* Filter category — pick one of: GO, Diseases, Lung, Platform.
          Click the active button again to deselect. */}
      <div className="filter-cat-group" role="group" aria-label="Filter category">
        <button
          type="button"
          className={`filter-cat-btn${activeFilterCategory === 'go' ? ' active' : ''}`}
          aria-pressed={activeFilterCategory === 'go'}
          onClick={() => handleCategoryClick('go')}
        >
          GO Annotations
        </button>
        {showDiseaseFilter && (
          <button
            type="button"
            className={`filter-cat-btn${activeFilterCategory === 'diseases' ? ' active' : ''}`}
            aria-pressed={activeFilterCategory === 'diseases'}
            onClick={() => handleCategoryClick('diseases')}
          >
            Diseases
          </button>
        )}
        <button
          type="button"
          className={`filter-cat-btn${activeFilterCategory === 'lung' ? ' active' : ''}`}
          aria-pressed={activeFilterCategory === 'lung'}
          onClick={() => handleCategoryClick('lung')}
        >
          Lung Tissue Specificity
        </button>
        <button
          type="button"
          className={`filter-cat-btn${activeFilterCategory === 'platform' ? ' active' : ''}`}
          aria-pressed={activeFilterCategory === 'platform'}
          onClick={() => handleCategoryClick('platform')}
        >
          Platform
        </button>
      </div>
      {activeFilterCategory !== 'none' && (
        <div style={{ marginTop: '8px' }} className="filter-cat-deselect-hint" >
          Click the selected filter again to disable it.
        </div>
      )}

      {/* GO Annotations: CC vs BP sub-toggle */}
      {activeFilterCategory === 'go' && (
        <div className="filter-mode-toggle" role="group" aria-label="GO term type" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', width: '100%' }}>
          <button
            className={`filter-mode-btn${goSubMode === 'cc' ? ' active' : ''}`}
            style={{ justifySelf: 'end' }}
            onClick={() => setGoSubMode('cc')}
            aria-pressed={goSubMode === 'cc'}
          >Cellular&nbsp;Component</button>
          <button
            className={`filter-mode-btn${goSubMode === 'bp' ? ' active' : ''}`}
            style={{ justifySelf: 'start' }}
            onClick={() => setGoSubMode('bp')}
            aria-pressed={goSubMode === 'bp'}
          >Biological&nbsp;Process</button>
        </div>
      )}

      {/* GO — Cellular Component chart */}
      {activeFilterCategory === 'go' && goSubMode === 'cc' && (
        <div style={{ width: '85%', maxWidth: 1100, margin: '18px auto 14px' }}>
          {/* Two equal halves so the seam between the buttons sits exactly on the
              page centre line, matching the Cellular Component / Biological Process
              toggle above regardless of the label widths. */}
          <div className="filter-mode-toggle" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', width: '100%', marginTop: 0, marginBottom: 16 }}>
            <button
              type="button"
              className={`filter-mode-btn${ccView === 'bar' ? ' active' : ''}`}
              style={{ justifySelf: 'end' }}
              onClick={() => setCcView('bar')}
              aria-pressed={ccView === 'bar'}
            >Bar&nbsp;chart</button>
            <button
              type="button"
              className={`filter-mode-btn${ccView === 'tree' ? ' active' : ''}`}
              style={{ justifySelf: 'start' }}
              onClick={() => setCcView('tree')}
              aria-pressed={ccView === 'tree'}
            >Graph</button>
          </div>
          <div style={{ textAlign: 'center', fontSize: '0.82rem', opacity: 0.75, marginBottom: 14 }}>
            {ccView === 'bar'
              ? 'Click a bar to toggle that cellular component as a filter.'
              : 'Click a box to filter on it and all its sub-terms. The graph collapses after a selection — reopen it to add more.'}
          </div>
          {ccView === 'bar' ? (
            <CcGoTermBarChart
              rowData={rowData}
              selectedGoIds={selectedCcGoTerms}
              onToggle={onCcGoTermToggle}
              isDark={darkMode}
            />
          ) : (
            <CcGoTermTreeFilter
              rowData={rowData}
              selectedGoIds={selectedCcGoTerms}
              onSetSelection={onCcGoSetSelection}
              isDark={darkMode}
            />
          )}
          {ccView === 'bar' && selectedCcGoTerms.size > 0 && (
            <div style={{ textAlign: 'center', marginTop: 14 }}>
              <button
                type="button"
                className="pill-btn"
                onClick={() => {
                  setSelectedCcGoTerms(new Set());
                  gridApi?.onFilterChanged();
                }}
              >
                Clear cellular component filter
              </button>
            </div>
          )}
        </div>
      )}

      {/* GO — Biological Process chart */}
      {activeFilterCategory === 'go' && goSubMode === 'bp' && (
        <div style={{ width: '85%', maxWidth: 1100, margin: '12px auto 10px' }}>
          <div style={{ textAlign: 'center', fontSize: '0.82rem', opacity: 0.75, marginBottom: 4 }}>
            Click a bar to toggle that biological process as a filter. Scroll horizontally to see all terms.
          </div>
          <BpGoTermBarChart
            rowData={rowData}
            selectedGoIds={selectedBpGoTerms}
            onToggle={onBpGoTermToggle}
            isDark={darkMode}
          />
          {selectedBpGoTerms.size > 0 && (
            <div style={{ textAlign: 'center', marginTop: 6 }}>
              <button
                type="button"
                className="pill-btn"
                onClick={() => {
                  setSelectedBpGoTerms(new Set());
                  gridApi?.onFilterChanged();
                }}
              >
                Clear biological process filter
              </button>
            </div>
          )}
        </div>
      )}

      {/* Diseases — MeSH hierarchy */}
      {showDiseaseFilter && activeFilterCategory === 'diseases' && (
        <>
          <div className="filter-mode-toggle mesh-view-toggle" role="group" aria-label="Hierarchy view">
            <button className={`filter-mode-btn mesh-view-btn${meshViewMode === 'tree' ? ' active' : ''}`}
              onClick={() => { setMeshViewMode('tree'); setMeshFilterSet(null); gridApi?.onFilterChanged(); }}>MeSH Tree</button>
            <button className={`filter-mode-btn mesh-view-btn${meshViewMode === 'classification' ? ' active' : ''}`}
              onClick={() => { setMeshViewMode('classification'); setMeshFilterSet(null); gridApi?.onFilterChanged(); }}>Classification</button>
          </div>
          {meshViewMode === 'tree' && (
            <MeshTreeFilter
              respiratoryOnly={meshTreeRespiratoryOnly}
              onRespiratoryOnlyChange={v => { setMeshTreeRespiratoryOnly(v); setMeshFilterSet(null); gridApi?.onFilterChanged(); }}
              onFilterChange={onMeshFilterChange}
              isDark={darkMode}
              knownIds={rowDataKnownIds}
            />
          )}
          {meshViewMode === 'sunburst' && (
            <MeshSunburstFilter respiratoryOnly={true} onFilterChange={onMeshFilterChange} isDark={darkMode} knownIds={rowDataKnownIds} />
          )}
          {meshViewMode === 'classification' && (
            <MeshClassificationFilter
              respiratoryOnly={meshClassificationRespiratoryOnly}
              onRespiratoryOnlyChange={v => { setMeshClassificationRespiratoryOnly(v); setMeshFilterSet(null); gridApi?.onFilterChanged(); }}
              onFilterChange={onMeshFilterChange}
              isDark={darkMode}
              knownIds={rowDataKnownIds}
            />
          )}
        </>
      )}

      {/* Lung Tissue Specificity */}
      {activeFilterCategory === 'lung' && (
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '4px 24px',
          flexWrap: 'wrap',
          justifyContent: 'center',
          marginTop: 12,
          marginBottom: 10,
        }}>
          {VISIBLE_LUNG_SPECIFICITY_CATEGORIES.map(cat => {
            const icon = SPECIFICITY_ICONS[cat];
            return (
              <label key={cat} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', userSelect: 'none', fontSize: '0.88rem' }}>
                <input
                  type="checkbox"
                  checked={selectedLungSpecificities.has(cat)}
                  onChange={e => onLungSpecificityToggle(cat, e.target.checked)}
                />
                {icon && <img src={icon} alt="" style={{ width: 16, height: 16 }} />}
                <span>{LUNG_SPECIFICITY_LABELS[cat]}</span>
              </label>
            );
          })}
        </div>
      )}

      {/* Platform */}
      {activeFilterCategory === 'platform' && (
        <div style={{ width: '85%', maxWidth: 1100, margin: '12px auto 10px' }}>
          <PlatformDonutChart
            rowData={rowData}
            selectedPlatforms={selectedPlatforms}
            onToggleSub={onPlatformSubToggle}
            onToggleGroup={onPlatformGroupToggle}
            isDark={darkMode}
          />
          {selectedPlatforms.size > 0 && (
            <div style={{ textAlign: 'center', marginTop: 6 }}>
              <button
                type="button"
                className="pill-btn"
                onClick={() => {
                  setSelectedPlatforms(new Set());
                  gridApi?.onFilterChanged();
                }}
              >
                Clear platform filter
              </button>
            </div>
          )}
        </div>
      )}


      <div id="myGrid" style={{ height: '800px', width: '85%', margin: '20px auto' }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          rowSelection={GRID_ROW_SELECTION}
          defaultColDef={GRID_DEFAULT_COL_DEF}
          onGridReady={onGridReady}
          onSelectionChanged={onSelectionChanged}
          onModelUpdated={params => updateDisplayedRowCount(params.api)}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          theme={gridTheme}
        />
      </div>

      <div
        style={{
          width: '85%',
          margin: '0 auto 12px auto',
          fontSize: '0.9rem',
          opacity: 0.9
        }}
      >
        Showing {displayedRowCount} of {rowData.length} proteins
        {selectedRowCount > 0 ? ` • ${selectedRowCount} selected` : ''}
      </div>

      <div className="powered-by-ibm-logo-container">
        <img src={ibmLogoDark} alt="Powered by IBM Cloud" />
      </div>

      {isAdmin && <AdminPanel open={showAdmin} onClose={() => setShowAdmin(false)} />}

      <ExportModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        columnDefs={columnDefs}
        gridApi={gridApi}
        selectedOnly={selectedRowCount > 0}
        onNotify={showNotification}
      />

      {notification && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            zIndex: 2000,
            minWidth: 280,
            maxWidth: 420,
            background: notification.type === 'error' ? '#7a1f1f' : '#1f5e3a',
            color: '#fff',
            padding: '12px 14px 12px 16px',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            fontSize: '0.9rem',
            lineHeight: 1.35
          }}
        >
          <span
            className="material-symbols-outlined"
            aria-hidden="true"
            style={{ fontSize: 22, lineHeight: 1, marginTop: 1 }}
          >
            {notification.type === 'error' ? 'error' : 'check_circle'}
          </span>
          <span style={{ flex: 1 }}>{notification.message}</span>
          <button
            type="button"
            onClick={() => setNotification(null)}
            aria-label="Dismiss notification"
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
              padding: '0 2px',
              opacity: 0.85
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
